import { createContext, useContext, useMemo } from 'react';
import { GymApparatus, GenderType, SessionType } from '../../models';
import { displayName } from '../../utilities/conversions';

// ============================================================================
// Context
// ============================================================================

export const SessionSetupContext = createContext(null);

export const useSessionSetup = () => {
  const context = useContext(SessionSetupContext);
  if (!context) {
    throw new Error('useSessionSetup must be used within SessionSetupContext');
  }
  return context;
};

// ============================================================================
// Apparatus Lists
// ============================================================================

export const getApparatusPossibilitiesByGender = (gender) =>
  gender === GenderType.FEMALE
    ? [
        GymApparatus.BYE1,
        GymApparatus.VT,
        GymApparatus.BYE2,
        GymApparatus.UB,
        GymApparatus.BYE3,
        GymApparatus.BB,
        GymApparatus.BYE4,
        GymApparatus.FX,
        GymApparatus.BYE5,
      ]
    : [
        GymApparatus.BYE1,
        GymApparatus.FX,
        GymApparatus.BYE2,
        GymApparatus.PH,
        GymApparatus.BYE3,
        GymApparatus.SR,
        GymApparatus.BYE4,
        GymApparatus.VT,
        GymApparatus.BYE5,
        GymApparatus.PB,
        GymApparatus.BYE6,
        GymApparatus.HB,
        GymApparatus.BYE7,
      ];

// Build apparatus list with byes at specified positions (1-indexed)
// byePositions is an array like [2, 5] meaning byes at 2nd and 5th columns
const buildApparatusListWithByes = (events, byePositions) => {
  const result = [];
  const sortedByes = [...byePositions].sort((a, b) => a - b);
  let eventIndex = 0;
  let byeCount = 0;

  for (let pos = 1; pos <= events.length + sortedByes.length; pos++) {
    if (sortedByes.includes(pos)) {
      byeCount++;
      result.push(GymApparatus[`BYE${byeCount}`]);
    } else {
      result.push(events[eventIndex]);
      eventIndex++;
    }
  }
  return result;
};

export const getApparatusList = (
  gender,
  alternating,
  squadCount,
  byePositions
) => {
  if (gender === GenderType.FEMALE) {
    const baseEvents = [
      GymApparatus.VT,
      GymApparatus.UB,
      GymApparatus.BB,
      GymApparatus.FX,
    ];

    if (alternating && squadCount === 3) {
      // Alternating tri: 4 events + 2 byes = 6 columns
      // Use custom bye positions if provided, otherwise default to [3, 5]
      const positions =
        byePositions && byePositions.length === 2 ? byePositions : [3, 5];
      return buildApparatusListWithByes(baseEvents, positions);
    }
    if (squadCount === 5) {
      // 5 squads: 4 events + 1 bye = 5 rotations
      // Use custom bye position if provided, otherwise default to position 4
      const position =
        byePositions && byePositions.length >= 1 ? [byePositions[0]] : [4];
      return buildApparatusListWithByes(baseEvents, position);
    }
    if (squadCount === 6) {
      // 6 squads: 4 events + 2 byes = 6 rotations
      // Use custom bye positions if provided, otherwise default to [2, 5]
      const positions =
        byePositions && byePositions.length === 2 ? byePositions : [2, 5];
      return buildApparatusListWithByes(baseEvents, positions);
    }
    if (squadCount === 7) {
      // 7 squads: 4 events + 3 byes = 7 rotations
      // Use custom bye positions if provided, otherwise default to [2, 4, 6]
      const positions =
        byePositions && byePositions.length === 3 ? byePositions : [2, 4, 6];
      return buildApparatusListWithByes(baseEvents, positions);
    }
    if (squadCount === 8) {
      return [
        GymApparatus.BYE1,
        GymApparatus.VT,
        GymApparatus.BYE2,
        GymApparatus.UB,
        GymApparatus.BYE3,
        GymApparatus.BB,
        GymApparatus.BYE4,
        GymApparatus.FX,
      ];
    }
    return [GymApparatus.VT, GymApparatus.UB, GymApparatus.BB, GymApparatus.FX];
  } else if (gender === GenderType.MALE) {
    if (alternating && squadCount === 4) {
      return [
        GymApparatus.BYE1,
        GymApparatus.FX,
        GymApparatus.PH,
        GymApparatus.SR,
        GymApparatus.BYE4,
        GymApparatus.VT,
        GymApparatus.PB,
        GymApparatus.HB,
      ];
    }
    if (squadCount === 7) {
      // 7 squads: 6 events + 1 bye = 7 rotations
      // Use custom bye position if provided, otherwise default to position 7 (after HB)
      const position =
        byePositions && byePositions.length >= 1 ? [byePositions[0]] : [7];
      return buildApparatusListWithByes(
        [
          GymApparatus.FX,
          GymApparatus.PH,
          GymApparatus.SR,
          GymApparatus.VT,
          GymApparatus.PB,
          GymApparatus.HB,
        ],
        position
      );
    }
    return [
      GymApparatus.FX,
      GymApparatus.PH,
      GymApparatus.SR,
      GymApparatus.VT,
      GymApparatus.PB,
      GymApparatus.HB,
    ];
  } else {
    throw new Error(`Unsupported session gender ${gender}`);
  }
};

// ============================================================================
// Sorting Hooks
// ============================================================================

export const useSortedSquads = () => {
  const session = useSessionSetup();
  return useMemo(
    () =>
      session.squads?.items
        ?.filter((s) => !s._deleted)
        ?.sort((a, b) => (a.name > b.name ? 1 : -1)) || [],
    [session.squads]
  );
};

export const useSortedSessionTeams = () => {
  const session = useSessionSetup();
  return useMemo(
    () =>
      session.sessionTeams?.items
        ?.filter((st) => !st._deleted)
        ?.sort((a, b) => (a.order > b.order ? 1 : -1)) || [],
    [session.sessionTeams]
  );
};

export const useSortedRotations = () => {
  const session = useSessionSetup();
  return useMemo(
    () =>
      session.rotations?.items
        ?.filter((rotation) => !rotation._deleted)
        ?.sort((a, b) => (a.order > b.order ? 1 : -1)) || [],
    [session.rotations]
  );
};

export const useSortedSessionRosters = () => {
  const session = useSessionSetup();
  return useMemo(() => {
    const squadNameById = {};
    for (const s of session.squads?.items || []) {
      if (!s._deleted) squadNameById[s.id] = s.name || '';
    }
    return (
      session.rosters?.items
        ?.filter((sr) => !sr._deleted)
        ?.sort((a, b) => {
          const aName =
            displayName(a.roster?.team?.name, a.roster?.team?.altNames) || '';
          const bName =
            displayName(b.roster?.team?.name, b.roster?.team?.altNames) || '';
          if (aName !== bName) return aName > bName ? 1 : -1;
          const aSquad = squadNameById[a.squadId] || 'ZZZ';
          const bSquad = squadNameById[b.squadId] || 'ZZZ';
          if (aSquad !== bSquad) return aSquad > bSquad ? 1 : -1;
          return (a.roster?.title || '') > (b.roster?.title || '') ? 1 : -1;
        }) || []
    );
  }, [session.rosters, session.squads]);
};

export const useSortedSessionRosterLineups = () => {
  const sortedSessionRosters = useSortedSessionRosters();
  return useMemo(() => sortedSessionRosters.filter((sr) => !!sr?.lineupId), [
    sortedSessionRosters,
  ]);
};

export const useSortedLineups = () => {
  const session = useSessionSetup();
  return useMemo(
    () =>
      session.lineups?.items
        ?.filter((lineup) => !lineup._deleted)
        ?.sort((a, b) => (a.order > b.order ? 1 : -1)) || [],
    [session.lineups]
  );
};

export const useSessionEvents = () => {
  const session = useSessionSetup();
  const squads = useSortedSquads();

  return useMemo(
    () => getApparatusList(session.gender, session.alternating, squads.length),
    [session.gender, session.alternating, squads.length]
  );
};

// ============================================================================
// Configuration Hook
// ============================================================================

export function useSessionConfiguration({
  type,
  squadCount,
  alternating,
  gender,
  byePositions,
}) {
  return useMemo(() => {
    let errorMessage;
    const squadCountInt = parseInt(squadCount, 10);

    // Determine actual squad count based on session type
    // DUAL: 1 squad (head-to-head) or 2 squads (alternating)
    // MULTI: use the squadCount from UI
    let actualSquadCount;
    if (type === SessionType.DUAL) {
      actualSquadCount = alternating ? 2 : 1;
    } else {
      actualSquadCount = squadCountInt;
    }

    const events = getApparatusList(
      gender,
      alternating,
      actualSquadCount,
      byePositions
    );

    const squads = ['A'];
    if (type === SessionType.DUAL) {
      if (alternating) {
        squads.push('B');
      }
    } else if (type === SessionType.MULTI && !isNaN(squadCountInt)) {
      if (squadCountInt > 2 * events.length) {
        errorMessage = `Can't have more squads (${squadCountInt}) than 2 flights of (${events.length}) events`;
      }

      for (let i = 1; i < Math.min(squadCountInt, 2 * events.length); i++) {
        squads.push(String.fromCharCode('A'.charCodeAt(0) + i));
      }
    }

    if (alternating && events.length % squads.length !== 0) {
      errorMessage = `Alternating format doesn't work for ${squads.length} squads with ${events.length} events!`;
    }

    const numRotations = Math.max(events.length, squads.length);

    const rotations = [];
    for (let r = 0; r < numRotations; r++) {
      rotations[r] = {};
      for (let s = 0; s < squads.length; s++) {
        let eventIndex;
        if (alternating) {
          eventIndex =
            squads.length * Math.floor(r / squads.length) +
            ((r + s) % squads.length);
        } else {
          eventIndex = (r + s) % events.length;
        }
        rotations[r][events[eventIndex]] = squads[s];
      }
    }

    return { squads, rotations, events, errorMessage };
  }, [type, squadCount, alternating, gender, byePositions]);
}

// ============================================================================
// Progress Calculation
// ============================================================================

export function useSetupProgress() {
  const session = useSessionSetup();
  const sortedSessionTeams = useSortedSessionTeams();
  const sortedSessionRosters = useSortedSessionRosters();
  const sortedSessionRosterLineups = useSortedSessionRosterLineups();
  const sortedRotations = useSortedRotations();
  const sortedSquads = useSortedSquads();
  const events = useSessionEvents();

  return useMemo(() => {
    // Info progress: type set, name set
    const infoScore = (session.type ? 1 : 0) + (session.name ? 1 : 0);
    const infoTotal = 2;
    const info = Math.round((infoScore / infoTotal) * 100);

    // Rosters progress: teams detected, rosters added, lineups linked
    // Only count "rostersMatchTeams" if there are actually teams to match
    const hasTeams = sortedSessionTeams.length > 0 ? 1 : 0;
    const rostersMatchTeams =
      sortedSessionTeams.length > 0 &&
      sortedSessionRosters.length >= sortedSessionTeams.length
        ? 1
        : 0;
    const allLineupsLinked =
      sortedSessionRosterLineups.length === sortedSessionRosters.length &&
      sortedSessionRosters.length > 0
        ? 1
        : 0;
    const rostersScore = hasTeams + rostersMatchTeams + allLineupsLinked;
    const rostersTotal = 3;
    const rosters = Math.round((rostersScore / rostersTotal) * 100);

    // Rotations progress: rotations exist, stages exist, squads exist, rosters in squads
    const hasRotations = sortedRotations.length >= events.length ? 1 : 0;

    // Check that rotations have stages - count total non-deleted stages
    const totalStages = sortedRotations.reduce((count, rotation) => {
      const activeStages =
        rotation.stages?.items?.filter((s) => !s._deleted) || [];
      return count + activeStages.length;
    }, 0);
    // For valid setup, each rotation has one stage per squad
    const expectedStages = sortedRotations.length * sortedSquads.length;
    const hasStages =
      sortedRotations.length > 0 && totalStages >= expectedStages ? 1 : 0;

    const hasSquads = sortedSquads.length > 0 ? 1 : 0;
    const rostersInSquads = sortedSessionRosters.filter((r) => r.squadId);
    const allRostersAssigned =
      rostersInSquads.length === sortedSessionRosters.length &&
      sortedSessionRosters.length > 0
        ? 1
        : 0;
    const rotationsScore =
      hasRotations + hasStages + hasSquads + allRostersAssigned;
    const rotationsTotal = 4;
    const rotationsProgress = Math.round(
      (rotationsScore / rotationsTotal) * 100
    );

    // Overall progress
    const overall = Math.round((info + rosters + rotationsProgress) / 3);

    return {
      info,
      rosters,
      rotations: rotationsProgress,
      overall,
    };
  }, [
    session.type,
    session.name,
    sortedSessionTeams,
    sortedSessionRosters,
    sortedSessionRosterLineups,
    sortedRotations,
    sortedSquads,
    events,
  ]);
}

// ============================================================================
// Utility Functions
// ============================================================================

export function sortRosters(rosters) {
  return rosters.slice().sort((a, b) => {
    // First priority: isDefault (true comes first)
    if (a.default !== b.default) {
      return b.default ? 1 : -1;
    }

    // Second priority: activeDate (most recent first)
    const aDate = a.activeDate ? new Date(a.activeDate) : new Date(0);
    const bDate = b.activeDate ? new Date(b.activeDate) : new Date(0);
    if (aDate.getTime() !== bDate.getTime()) {
      return bDate.getTime() - aDate.getTime();
    }

    // Third priority: title alphabetically
    return (a.title || '').localeCompare(b.title || '');
  });
}

export function getStagesByApparatus(stages) {
  return stages.reduce((acc, stage) => {
    if (!stage._deleted) {
      acc[stage.apparatus] = stage;
    }
    return acc;
  }, {});
}

export function getSessionRosterName(sessionRoster) {
  if (!sessionRoster?.roster) return 'Unknown';
  const team = sessionRoster.roster.team;
  const title = sessionRoster.roster.title;
  return team ? `${team.name} (${title})` : title;
}
